import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import App from './App';
import './designSystem.css';
import './index.css';

// Suppress harmless ResizeObserver errors during route transitions
// These errors occur when navigating to/from Stripe checkout and are completely harmless
const resizeObserverLoopErrRe = /ResizeObserver loop limit exceeded|ResizeObserver loop completed with undelivered notifications/;

// Suppress console.error logs
const originalError = console.error;
console.error = (...args) => {
  if (args[0] && typeof args[0] === 'string' && !resizeObserverLoopErrRe.test(args[0])) {
    originalError(...args);
  }
};

// Suppress runtime errors at the window level
const originalErrorHandler = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  if (typeof message === 'string' && resizeObserverLoopErrRe.test(message)) {
    return true; // Prevent default error handling
  }
  if (originalErrorHandler) {
    return originalErrorHandler(message, source, lineno, colno, error);
  }
  return false;
};

/**
 * Auth0ProviderWithNavigate
 * 
 * Wraps Auth0Provider with React Router's useNavigate for proper SPA navigation.
 * This is the Auth0 recommended pattern for React Router v6+.
 */
const Auth0ProviderWithNavigate = ({ children }) => {
  const navigate = useNavigate();

  // Auth0 recommended callback handler using React Router
  const onRedirectCallback = (appState) => {
    // Navigate using React Router - no page reload!
    navigate(appState?.returnTo || window.location.pathname || '/app', {
      replace: true
    });
  };

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email read:current_user update:current_user_metadata'
      }}
      onRedirectCallback={onRedirectCallback}
      // IMPORTANT: Let Auth0 SDK handle callback route automatically
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      {children}
    </Auth0Provider>
  );
};

ReactDOM.createRoot(document.getElementById('root')).render(
  <BrowserRouter>
    <Auth0ProviderWithNavigate>
      <App />
    </Auth0ProviderWithNavigate>
  </BrowserRouter>
);
